import { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { flushSync } from 'react-dom'
import { useAppDispatch } from '@/src/redux/store'
import { setAuthUser, refreshAuthUser, clearAuthUser } from '@/src/redux/slices/authSlice'
import { deleteCookie } from '@/src/utils/browser'
import {
    useSendOtpMutation,
    useVerifyOtpMutation,
    useSendEmailOtpMutation,
    useVerifyEmailOtpMutation
} from '@/src/services/otpAuthApi'
import { useLazyFetchUserDetailsQuery } from '@/src/services/userDetailsApi'
import { useGetCountryDataQuery, CountryWithMCC } from '@/src/api/rtkQuery/countryApi'
import { useUserLogoutMutation } from '@/src/api/rtkQuery/userApi'
import { getMobileValidationLength } from '@/src/utils/generalUtils/mobileValidation'
import { mapOtpError, UITreatment } from '@/src/services/otpErrorMapping'
import { GoogleUserData } from '@/src/types/auth'
import { fetchAndMapUserDetails } from '@/src/utils/auth/userDetailsMapper'
import isAeDomain from '@/src/utils/generalUtils/isAeDomain'
import isEgDomain from '@/src/utils/generalUtils/isEgDomain'
import { getBrandName } from '@/src/utils/buildUtils'
import { invokeCustomEvent } from '@/src/utils/analytics/clevertap'
import { useErrorHandler } from './useErrorHandler'
import { useResendTimer } from './useResendTimer'
import useClevertapForSignin from './analytics/clevertap/signin'

// OTP Flow Steps
export enum OtpStep {
    MOBILE_INPUT = 'mobile_input',
    MOBILE_VERIFICATION = 'mobile_verification',
    EMAIL_INPUT = 'email_input',
    EMAIL_VERIFICATION = 'email_verification',
    GOOGLE_MOBILE_INPUT = 'google_mobile_input'
}

interface UseOtpFlowOptions {
    onClose: () => void
    onMenuClose?: () => void
    translate?: (key: string) => string
    isMobile?: boolean
}

export function useOtpFlow({ onClose, onMenuClose, translate, isMobile = false }: UseOtpFlowOptions) {
    const dispatch = useAppDispatch()

    // Default translation function
    const t = translate || ((key: string) => key)

    // Error handling hook - replaces scattered error state management
    const { error, errorState, setError, clearError, handleApiError, handleOtpError } = useErrorHandler({
        translate: t
    })

    // Timer hooks - replaces duplicate timer management code
    const mobileTimer = useResendTimer({ duration: 30 })
    const emailTimer = useResendTimer({ duration: 30 })

    // Store timer objects in refs to avoid them being dependencies
    const mobileTimerRef = useRef(mobileTimer)
    const emailTimerRef = useRef(emailTimer)

    // Update refs when timer objects change
    useEffect(() => {
        mobileTimerRef.current = mobileTimer
    }, [mobileTimer])

    useEffect(() => {
        emailTimerRef.current = emailTimer
    }, [emailTimer])

    // Flow state
    const [currentStep, setCurrentStep] = useState<OtpStep>(OtpStep.MOBILE_INPUT)

    // Get default country code based on domain
    // FEG (Egypt) -> 20, FAE (UAE) -> 971, FSA (Saudi Arabia) -> 966
    const getDefaultCountryCode = () => {
        if (isEgDomain()) {
            return '20' // Egypt
        }
        if (isAeDomain()) {
            return '971' // UAE
        }
        return '966' // Saudi Arabia (default)
    }

    // Mobile verification state
    const [mobileCountryCode, setMobileCountryCode] = useState(getDefaultCountryCode())
    const [mobileNumber, setMobileNumber] = useState('')
    const [mobileOtp, setMobileOtp] = useState('')

    // Email verification state
    const [email, setEmail] = useState('')
    const [emailOtp, setEmailOtp] = useState('')
    const [existingEmail, setExistingEmail] = useState<string | null>(null)
    const [isWelcomeBack, setIsWelcomeBack] = useState(false)

    // Google Sign-In state
    const [googleUserData, setGoogleUserData] = useState<GoogleUserData | null>(null)
    const [isGoogleFlow, setIsGoogleFlow] = useState(false)

    // Track if verification is in progress to prevent duplicate calls
    const isVerifyingMobileRef = useRef(false)
    const isVerifyingEmailRef = useRef(false)

    // Track setTimeout IDs for cleanup
    const mobileVerifyTimeoutRef = useRef<NodeJS.Timeout | null>(null)
    const emailVerifyTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    // API hooks
    const [sendOtp, { isLoading: isSendingOtp }] = useSendOtpMutation()
    const [verifyOtp, { isLoading: isVerifyingOtp }] = useVerifyOtpMutation()
    const [sendEmailOtp, { isLoading: isSendingEmailOtp }] = useSendEmailOtpMutation()
    const [verifyEmailOtp, { isLoading: isVerifyingEmailOtp }] = useVerifyEmailOtpMutation()
    const [fetchUserDetails] = useLazyFetchUserDetailsQuery()
    const [triggerLogout] = useUserLogoutMutation()
    const { data: countryData, isLoading: isCountryDataLoading } = useGetCountryDataQuery()

    // CleverTap analytics for signin events
    const { triggerSigninEvent } = useClevertapForSignin()
    const triggerCleverTapLogin = useCallback(() => {
        invokeCustomEvent('CLEVERTAP_PROFILE', { type: 'login' })
    }, [])

    // Find selected country code option (memoized to prevent unnecessary recalculations)
    const selectedCountryCode = useMemo(
        () => countryData?.find(option => option.mobileCountryCode === mobileCountryCode) || null,
        [countryData, mobileCountryCode]
    )

    // Mobile number validation
    const validateMobileNumber = useCallback(
        (mobile: string, countryCode: string) => {
            const phoneRegex = /^\d*$/
            if (!phoneRegex.test(mobile)) {
                return t('pleaseEnterValidPhoneNumber')
            }

            const validation = getMobileValidationLength(countryCode)
            if (mobile.length < validation.min || mobile.length > validation.max) {
                return t('pleaseEnterValidPhoneNumber')
            }
            return null
        },
        [t]
    )

    // Email validation
    const validateEmail = useCallback(
        (emailValue: string) => {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
            if (!emailRegex.test(emailValue)) {
                return t('pleaseEnterValidEmailAddress')
            }
            return null
        },
        [t]
    )

    const handleOnCross = useCallback(async () => {
        // Clear timers
        mobileTimerRef.current.stop()
        emailTimerRef.current.stop()

        // Clear pending verification timeouts
        if (mobileVerifyTimeoutRef.current) {
            clearTimeout(mobileVerifyTimeoutRef.current)
            mobileVerifyTimeoutRef.current = null
        }
        if (emailVerifyTimeoutRef.current) {
            clearTimeout(emailVerifyTimeoutRef.current)
            emailVerifyTimeoutRef.current = null
        }
        // If Google flow was in progress but not completed, clear auth state and cookies
        // This prevents auth state from persisting after reload if user closed before OTP verification
        // Backend may have set cookies during Google sign-in API call, so we need to clear them
        if (isGoogleFlow) {
            try {
                // Call logout API to clean up backend session
                await triggerLogout({ brand: getBrandName() }).unwrap()
            } catch (error) {
                console.error('Logout failed during Google flow cleanup:', error)
            }

            // Clear redux state (always execute, even if logout API failed)
            dispatch(clearAuthUser())

            // Remove cookies (always execute, even if logout API failed)
            if (typeof window !== 'undefined') {
                const domain = '.' + window.location.hostname
                deleteCookie('usermisc', domain)
                deleteCookie('userid', domain)
                deleteCookie('userData', domain)
            }
            // Refresh user state (always execute, even if logout API failed)
            dispatch(refreshAuthUser())
        }

        setCurrentStep(OtpStep.MOBILE_INPUT)
        clearError()
        setMobileCountryCode(getDefaultCountryCode())
        setMobileNumber('')
        setMobileOtp('')
        setEmail('')
        setEmailOtp('')
        setGoogleUserData(null)
        setIsGoogleFlow(false)
        setIsWelcomeBack(false)
        setExistingEmail(null)
        onClose()
    }, [dispatch, triggerLogout, isGoogleFlow])

    // Close handler with reset
    const handleClose = useCallback(async () => {
        // Clear timers
        mobileTimerRef.current.stop()
        emailTimerRef.current.stop()

        // Clear pending verification timeouts
        if (mobileVerifyTimeoutRef.current) {
            clearTimeout(mobileVerifyTimeoutRef.current)
            mobileVerifyTimeoutRef.current = null
        }
        if (emailVerifyTimeoutRef.current) {
            clearTimeout(emailVerifyTimeoutRef.current)
            emailVerifyTimeoutRef.current = null
        }

        setCurrentStep(OtpStep.MOBILE_INPUT)
        clearError()
        setMobileCountryCode(getDefaultCountryCode())
        setMobileNumber('')
        setMobileOtp('')
        setEmail('')
        setEmailOtp('')
        setGoogleUserData(null)
        setIsGoogleFlow(false)
        setIsWelcomeBack(false)
        setExistingEmail(null)
        onClose()
    }, [onClose, clearError, isGoogleFlow, currentStep, dispatch])

    // Step 1: Send mobile OTP
    const handleSendMobileOtp = useCallback(async () => {
        const validationError = validateMobileNumber(mobileNumber, mobileCountryCode)
        if (validationError) {
            setError(validationError)
            return
        }

        try {
            const response = await sendOtp({
                type: 'MOBILE',
                value: mobileNumber,
                countryCode: `+${mobileCountryCode}`,
                action: 'SIGNIN'
            }).unwrap()

            if (response.status?.toLowerCase() === 'success') {
                setCurrentStep(OtpStep.MOBILE_VERIFICATION)
                clearError()
                mobileTimerRef.current.start()
                // Track send OTP success - mobile step, user not verified yet
                triggerSigninEvent('Air_UI_Action', {
                    action_name: 'send_otp_mobile',
                    action_type: 'BUTTON',
                    action_value: 'success'
                })
            } else if (response.error) {
                const errorMapping = mapOtpError(response.error, t)
                setError(errorMapping.message)
                triggerSigninEvent('Air_UI_Action', {
                    action_name: 'send_otp_mobile',
                    action_type: 'BUTTON',
                    action_value: 'fail'
                })
            }
        } catch (err: any) {
            handleApiError(err, t('failedToSendOtp'))
            triggerSigninEvent('Air_UI_Action', {
                action_name: 'send_otp_mobile',
                action_type: 'BUTTON',
                action_value: 'fail'
            })
        }
    }, [mobileNumber, mobileCountryCode, sendOtp, validateMobileNumber, handleApiError, t, clearError, setError])

    // Step 2: Verify mobile OTP
    const handleVerifyMobileOtp = useCallback(async () => {
        if (isVerifyingMobileRef.current) return

        const cleanedOtp = mobileOtp.replace(/\D/g, '')
        if (!cleanedOtp || cleanedOtp.length !== 4) {
            setError(t('pleaseEnterValid4DigitOtp'))
            return
        }

        clearError()
        isVerifyingMobileRef.current = true

        try {
            const response = await verifyOtp({
                type: 'MOBILE',
                value: mobileNumber,
                countryCode: `+${mobileCountryCode}`,
                action: 'SIGNIN',
                otp: cleanedOtp
            }).unwrap()

            if (response.action?.toLowerCase() === 'signin' || response.action?.toLowerCase() === 'linked_signup') {
                try {
                    const userDetails = await fetchUserDetails().unwrap()
                    // Track submit OTP success with userDetails
                    triggerSigninEvent('Air_UI_Action', {
                        action_name: 'submit_otp_mobile',
                        action_type: 'BUTTON',
                        email_verified: userDetails.emailVerified ?? null,
                        mobile_verified: userDetails.mobileVerified ?? true,
                        action_value: 'success'
                    })

                    if (!userDetails.emailVerified) {
                        // CRITICAL FIX: Use flushSync to ensure step change is committed before auth dispatch
                        // This prevents mobile drawer from closing when transitioning to EMAIL_INPUT step
                        flushSync(() => {
                            if (userDetails.username) {
                                setExistingEmail(userDetails.username)
                                setEmail("")
                                setIsWelcomeBack(true)
                            } else {
                                setExistingEmail(null)
                                setEmail('')
                                setIsWelcomeBack(false)
                            }

                            setCurrentStep(OtpStep.EMAIL_INPUT)
                            isVerifyingMobileRef.current = false
                        })

                        // Close the user menu to prevent it from opening when auth state changes
                        onMenuClose?.()

                        const authState = await fetchAndMapUserDetails(() => Promise.resolve(userDetails), {
                            mobile: mobileNumber,
                            countryCode: mobileCountryCode
                        })

                        // CRITICAL FIX: Defer auth dispatch to next event loop tick
                        // This allows UI to stabilize at EMAIL_INPUT step before auth state changes
                        // Without this setTimeout, the mobile drawer unmounts due to re-render from dispatch
                        setTimeout(() => {
                            dispatch(setAuthUser(authState))
                        }, 0)

                        return
                    }

                    const authState = await fetchAndMapUserDetails(() => Promise.resolve(userDetails), {
                        mobile: mobileNumber,
                        countryCode: mobileCountryCode
                    })
                    dispatch(setAuthUser(authState))

                    onMenuClose?.()
                    handleClose()
                    isVerifyingMobileRef.current = false
                    return
                } catch (err: any) {
                    clearError()
                    // Track submit OTP success even if userDetails fetch fails (mobile verified)
                    triggerSigninEvent('Air_UI_Action', {
                        action_name: 'submit_otp_mobile',
                        action_type: 'BUTTON',
                        email_verified: null,
                        mobile_verified: true,
                        action_value: 'success'
                    })

                    if (err?.originalStatus === 404 || err?.status === 404) {
                        // CRITICAL FIX: Use flushSync to ensure step change is committed before auth dispatch
                        // This prevents mobile drawer from closing when transitioning to EMAIL_INPUT step
                        flushSync(() => {
                            setExistingEmail(null)
                            setEmail('')
                            setIsWelcomeBack(false)
                            setCurrentStep(OtpStep.EMAIL_INPUT)
                            isVerifyingMobileRef.current = false
                        })

                        // Close the user menu to prevent it from opening when auth state changes
                        onMenuClose?.()

                        // CRITICAL FIX: Defer auth dispatch to next event loop tick
                        // This allows UI to stabilize at EMAIL_INPUT step before auth state changes
                        // Without this setTimeout, the mobile drawer unmounts due to re-render from dispatch
                        setTimeout(() => {
                            dispatch(
                                setAuthUser({
                                    signedIn: true,
                                    mobile: mobileNumber,
                                    countryCode: mobileCountryCode,
                                    displayName: `+${mobileCountryCode} ${mobileNumber}`
                                })
                            )
                        }, 0)

                        return
                    }

                    dispatch(
                        setAuthUser({
                            signedIn: true,
                            mobile: mobileNumber,
                            countryCode: mobileCountryCode,
                            displayName: `+${mobileCountryCode} ${mobileNumber}`
                        })
                    )

                    onMenuClose?.()
                    handleClose()
                    isVerifyingMobileRef.current = false
                    return
                }
            } else if (response.action?.toLowerCase() === 'signup') {
                // Track submit OTP success for signup (new user, mobile verified)
                triggerSigninEvent('Air_UI_Action', {
                    action_name: 'submit_otp_mobile',
                    action_type: 'BUTTON',
                    email_verified: null,
                    mobile_verified: true,
                    action_value: 'success'
                })
                // CRITICAL FIX: Use flushSync to ensure step change is committed before auth dispatch
                // This prevents mobile drawer from closing when transitioning to EMAIL_INPUT step
                flushSync(() => {
                    setExistingEmail(null)
                    setEmail('')
                    setIsWelcomeBack(false)
                    setCurrentStep(OtpStep.EMAIL_INPUT)
                    setMobileOtp('')
                    isVerifyingMobileRef.current = false
                })

                // Close the user menu to prevent it from opening when auth state changes
                onMenuClose?.()

                // CRITICAL FIX: Defer auth dispatch to next event loop tick
                // This allows UI to stabilize at EMAIL_INPUT step before auth state changes
                // Without this setTimeout, the mobile drawer unmounts due to re-render from dispatch
                setTimeout(() => {
                    dispatch(
                        setAuthUser({
                            signedIn: true,
                            mobile: mobileNumber,
                            countryCode: mobileCountryCode,
                            displayName: `+${mobileCountryCode} ${mobileNumber}`
                        })
                    )
                }, 0)

                return
            } else if ((response as any).error) {
                const errorMapping = mapOtpError((response as any).error, t)
                setError(errorMapping.message)
                triggerSigninEvent('Air_UI_Action', {
                    action_name: 'submit_otp_mobile',
                    action_type: 'BUTTON',
                    email_verified: null,
                    mobile_verified: null,
                    action_value: 'fail'
                })
                isVerifyingMobileRef.current = false
                return
            } else {
                setError(t('somethingWentWrong'))
                triggerSigninEvent('Air_UI_Action', {
                    action_name: 'submit_otp_mobile',
                    action_type: 'BUTTON',
                    email_verified: null,
                    mobile_verified: null,
                    action_value: 'fail'
                })
                isVerifyingMobileRef.current = false
                return
            }
        } catch (err: any) {
            handleOtpError(err)
            triggerSigninEvent('Air_UI_Action', {
                action_name: 'submit_otp_mobile',
                action_type: 'BUTTON',
                email_verified: null,
                mobile_verified: null,
                action_value: 'fail'
            })
            isVerifyingMobileRef.current = false
        }
    }, [
        mobileOtp,
        mobileNumber,
        mobileCountryCode,
        verifyOtp,
        triggerSigninEvent,
        dispatch,
        fetchUserDetails,
        handleApiError,
        t,
        handleClose,
        onMenuClose,
        isMobile,
        setError,
        clearError,
        handleOtpError,
        fetchAndMapUserDetails
    ])

    // Step 3: Send email OTP
    const handleSendEmailOtp = useCallback(async () => {
        if (isWelcomeBack && existingEmail && email !== existingEmail) {
            setError(t('emailAddressDoesNotMatch'))
            return
        }
        const validationError = validateEmail(email)
        if (validationError) {
            setError(validationError)
            return
        }

        try {
            const response = await sendEmailOtp({
                value: email,
                action: 'SIGNUP_MERGE'
            }).unwrap()

            if (response.status?.toLowerCase() === 'success' || response.status?.toLowerCase() === 'accepted') {
                setCurrentStep(OtpStep.EMAIL_VERIFICATION)
                clearError()
                emailTimerRef.current.start()
                // Track send OTP success - email step (mobile verified, email not yet)
                triggerSigninEvent('Air_UI_Action', {
                    action_name: 'send_otp_email',
                    action_type: 'BUTTON',
                    email_verified: false,
                    mobile_verified: true,
                    action_value: 'success'
                })
            } else if ((response as any).error) {
                const errorMapping = mapOtpError((response as any).error, t)
                setError(errorMapping.message)
                triggerSigninEvent('Air_UI_Action', {
                    action_name: 'send_otp_email',
                    action_type: 'BUTTON',
                    email_verified: false,
                    mobile_verified: true,
                    action_value: 'fail'
                })
            }
        } catch (err: any) {
            handleApiError(err, t('failedToSendOtp'))
            triggerSigninEvent('Air_UI_Action', {
                action_name: 'send_otp_email',
                action_type: 'BUTTON',
                email_verified: false,
                mobile_verified: true,
                action_value: 'fail'
            })
        }
    }, [
        email,
        existingEmail,
        isWelcomeBack,
        sendEmailOtp,
        validateEmail,
        handleApiError,
        t,
        clearError,
        setError,
        triggerSigninEvent
    ])

    // Step 4: Verify email OTP
    const handleVerifyEmailOtp = useCallback(async () => {
        // Prevent duplicate calls
        if (isVerifyingEmailRef.current) return

        const cleanedOtp = emailOtp.replace(/\D/g, '')
        if (!cleanedOtp || cleanedOtp.length !== 4) {
            setError(t('pleaseEnterValid4DigitOtp'))
            return
        }

        clearError()

        // Mark as verifying
        isVerifyingEmailRef.current = true

        try {
            const response = await verifyEmailOtp({
                value: email,
                otp: cleanedOtp,
                action: 'SIGNUP_MERGE'
            }).unwrap()

            if (
                response.action?.toLowerCase() === 'signin' ||
                response.action?.toLowerCase() === 'signup' ||
                response.action?.toLowerCase() === 'signup_merge'
            ) {
                // Track submit OTP success - both email and mobile now verified
                triggerSigninEvent('Air_UI_Action', {
                    action_name: 'submit_otp_email',
                    action_type: 'BUTTON',
                    email_verified: true,
                    mobile_verified: true,
                    action_value: 'success'
                })
                // Immediately update auth state before closing modal
                try {
                    const authState = await fetchAndMapUserDetails(() => fetchUserDetails().unwrap(), {
                        mobile: mobileNumber,
                        countryCode: mobileCountryCode,
                        email: email
                    })
                    dispatch(setAuthUser(authState))
                    triggerCleverTapLogin()
                } catch (err: any) {
                    dispatch(
                        setAuthUser({
                            signedIn: true,
                            mobile: mobileNumber,
                            countryCode: mobileCountryCode,
                            email: email,
                            displayName: mobileNumber ? `+${mobileCountryCode} ${mobileNumber}` : email
                        })
                    )
                    triggerCleverTapLogin()
                }

                // Close menu first, then close modal to prevent flickering
                onMenuClose?.()
                handleClose()
                isVerifyingEmailRef.current = false
            } else if ((response as any).error) {
                const errorMapping = mapOtpError((response as any).error, t)
                setError(errorMapping.message)
                triggerSigninEvent('Air_UI_Action', {
                    action_name: 'submit_otp_email',
                    action_type: 'BUTTON',
                    email_verified: false,
                    mobile_verified: true,
                    action_value: 'fail',
                    error_message: errorMapping.message,
                    response: response
                })
                isVerifyingEmailRef.current = false
            }
        } catch (err: any) {
            handleOtpError(err)
            triggerSigninEvent('Air_UI_Action', {
                action_name: 'submit_otp_email',
                action_type: 'BUTTON',
                email_verified: false,
                mobile_verified: true,
                action_value: 'fail',
                error_message: 'Something went wrong with email OTP verification'
            })
            isVerifyingEmailRef.current = false
        }
    }, [
        emailOtp,
        email,
        mobileNumber,
        mobileCountryCode,
        verifyEmailOtp,
        dispatch,
        fetchUserDetails,
        handleClose,
        onMenuClose,
        setError,
        clearError,
        handleOtpError,
        t,
        fetchAndMapUserDetails,
        triggerSigninEvent,
        triggerCleverTapLogin
    ])

    // Resend OTP handlers
    const handleResendMobileOtp = useCallback(async () => {
        if (mobileTimerRef.current.isRunning) return

        setMobileOtp('')
        clearError()

        try {
            const response = await sendOtp({
                type: 'MOBILE',
                value: mobileNumber,
                countryCode: `+${mobileCountryCode}`,
                action: 'SIGNIN'
            }).unwrap()

            if (response.status?.toLowerCase() === 'success') {
                clearError()
                mobileTimerRef.current.start()
            } else if (response.error) {
                const errorMapping = mapOtpError(response.error, t)
                setError(errorMapping.message)
            }
        } catch (err: any) {
            handleApiError(err, t('failedToResendOtp'))
        }
    }, [mobileNumber, mobileCountryCode, sendOtp, handleApiError, t, clearError, setError])

    const handleResendEmailOtp = useCallback(async () => {
        if (emailTimerRef.current.isRunning) return

        setEmailOtp('')
        clearError()

        try {
            const response = await sendEmailOtp({
                value: email,
                action: 'SIGNUP_MERGE'
            }).unwrap()

            if (response.status?.toLowerCase() === 'success' || response.status?.toLowerCase() === 'accepted') {
                clearError()
                emailTimerRef.current.start()
            } else if ((response as any).error) {
                const errorMapping = mapOtpError((response as any).error, t)
                setError(errorMapping.message)
            }
        } catch (err: any) {
            handleApiError(err, t('failedToResendOtp'))
        }
    }, [email, sendEmailOtp, handleApiError, t, clearError, setError])

    // Navigation handlers
    const handleBackToMobileInput = useCallback(() => {
        // If in Google flow, go back to Google mobile input step, otherwise regular mobile input
        setCurrentStep(isGoogleFlow ? OtpStep.GOOGLE_MOBILE_INPUT : OtpStep.MOBILE_INPUT)
        setMobileOtp('')
        clearError()
        // Reset verification flag when going back
        isVerifyingMobileRef.current = false
    }, [clearError, isGoogleFlow])

    const handleBackToEmailInput = useCallback(() => {
        setCurrentStep(OtpStep.EMAIL_INPUT)
        setEmailOtp('')
        clearError()
        // Reset verification flag when going back
        isVerifyingEmailRef.current = false
    }, [clearError])

    // Input change handlers
    const handleCountryCodeChange = useCallback(
        (country: CountryWithMCC | null) => {
            if (country) {
                setMobileCountryCode(country.mobileCountryCode)
                clearError()
            }
        },
        [clearError]
    )

    const handleMobileNumberChange = useCallback(
        (value: string) => {
            const digitsOnly = value.replace(/\D/g, '')
            setMobileNumber(digitsOnly)
            clearError()
        },
        [clearError]
    )

    const handleEmailChange = useCallback(
        (value: string) => {
            setEmail(value.trim())
            clearError()
        },
        [clearError]
    )

    const handleMobileOtpChange = useCallback(
        (value: string) => {
            setMobileOtp(value)
            clearError()
            // Reset verification flag when user changes OTP
            isVerifyingMobileRef.current = false
        },
        [clearError]
    )

    const handleEmailOtpChange = useCallback(
        (value: string) => {
            setEmailOtp(value)
            clearError()
            // Reset verification flag when user changes OTP
            isVerifyingEmailRef.current = false
        },
        [clearError]
    )

    // Handle Google Sign-In
    const handleGoogleSignIn = useCallback(
        async (userData: GoogleUserData) => {
            try {
                // Store Google user data
                setGoogleUserData(userData)
                setIsGoogleFlow(true)
                setEmail(userData.email) // Pre-fill email from Google
                clearError()

                // Check if user already has a mobile number
                if (userData.mobile && userData.countryCode) {
                    // User already has mobile - skip mobile input and complete authentication
                    setMobileNumber(userData.mobile)
                    setMobileCountryCode(userData.countryCode)

                    // Try to fetch complete user details
                    try {
                        const authState = await fetchAndMapUserDetails(() => fetchUserDetails().unwrap(), {
                            mobile: userData.mobile,
                            countryCode: userData.countryCode,
                            email: userData.email
                        })

                        // Add Google-specific fields to the auth state
                        const googleAuthState = {
                            ...authState,
                            picture: userData.picture,
                            googleId: userData.googleId,
                            userId: userData.userId,
                            ctAuth: userData.ctAuth
                        }

                        dispatch(setAuthUser(googleAuthState))
                        triggerCleverTapLogin()
                        // Track Google sign in success with user having mobile
                        triggerSigninEvent('Air_UI_Action', {
                            action_name: 'google_sign_in',
                            action_type: 'BUTTON',
                            email_verified: true,
                            mobile_verified: true,
                            action_value: 'success'
                        })
                    } catch (err: any) {
                        dispatch(
                            setAuthUser({
                                signedIn: true,
                                mobile: userData.mobile,
                                countryCode: userData.countryCode,
                                email: userData.email,
                                firstName: userData.firstName,
                                lastName: userData.lastName,
                                fullName: userData.fullName,
                                picture: userData.picture,
                                googleId: userData.googleId,
                                userId: userData.userId,
                                ctAuth: userData.ctAuth,
                                displayName: userData.fullName || userData.email
                            })
                        )
                        triggerCleverTapLogin()
                        // Track Google sign in success (user details fetch failed but auth succeeded)
                        triggerSigninEvent('Air_UI_Action', {
                            action_name: 'google_sign_in',
                            action_type: 'BUTTON',
                            email_verified: true,
                            mobile_verified: true,
                            action_value: 'success'
                        })
                    }

                    // Close menu first, then close modal to prevent flickering
                    onMenuClose?.()
                    handleClose()
                } else {
                    // No mobile number - proceed to mobile input step
                    // Track Google sign in - needs mobile verification
                    triggerSigninEvent('Air_UI_Action', {
                        action_name: 'google_sign_in',
                        action_type: 'BUTTON',
                        email_verified: true,
                        mobile_verified: false,
                        action_value: 'success'
                    })
                    // Don't close menu yet - we're transitioning to mobile input
                    setCurrentStep(OtpStep.GOOGLE_MOBILE_INPUT)
                }
            } catch (error) {
                setError(t('failedToSignInWithGoogle'))
                triggerSigninEvent('Air_UI_Action', {
                    action_name: 'google_sign_in',
                    action_type: 'BUTTON',
                    email_verified: null,
                    mobile_verified: null,
                    action_value: 'fail'
                })
            }
        },
        [
            setError,
            clearError,
            t,
            dispatch,
            fetchUserDetails,
            handleClose,
            onMenuClose,
            fetchAndMapUserDetails,
            setMobileNumber,
            setMobileCountryCode,
            triggerSigninEvent,
            triggerCleverTapLogin
        ]
    )

    // Handle Google Sign In error (when user cancels or OAuth fails)
    const handleGoogleSignInError = useCallback(() => {
        triggerSigninEvent('Air_UI_Action', {
            action_name: 'google_sign_in',
            action_type: 'BUTTON',
            email_verified: null,
            mobile_verified: null,
            action_value: 'fail'
        })
    }, [triggerSigninEvent])

    // Handle sending mobile OTP in Google flow
    const handleSendGoogleMobileOtp = useCallback(async () => {
        const validationError = validateMobileNumber(mobileNumber, mobileCountryCode)
        if (validationError) {
            setError(validationError)
            return
        }

        try {
            const response = await sendOtp({
                type: 'MOBILE',
                value: mobileNumber,
                countryCode: `+${mobileCountryCode}`,
                action: 'UPDATE_MOBILE'
            }).unwrap()

            if (response.status?.toLowerCase() === 'success') {
                setCurrentStep(OtpStep.MOBILE_VERIFICATION)
                clearError()
                mobileTimerRef.current.start()
            } else if (response.error) {
                const errorMapping = mapOtpError(response.error, t)
                setError(errorMapping.message)
            }
        } catch (err: any) {
            handleApiError(err, t('failedToSendOtp'))
        }
    }, [mobileNumber, mobileCountryCode, sendOtp, validateMobileNumber, handleApiError, t, clearError, setError])

    // Handle verifying mobile OTP in Google flow - completes the registration
    const handleVerifyGoogleMobileOtp = useCallback(async () => {
        // Prevent duplicate calls
        if (isVerifyingMobileRef.current) return

        const cleanedOtp = mobileOtp.replace(/\D/g, '')
        if (!cleanedOtp || cleanedOtp.length !== 4) {
            setError(t('pleaseEnterValid4DigitOtp'))
            return
        }

        // Clear error before API call
        clearError()

        // Validate Google data is available before API call
        if (!googleUserData) {
            setError(t('googleSignInDataMissing'))
            return
        }

        // Mark as verifying
        isVerifyingMobileRef.current = true

        try {
            const response = await verifyOtp({
                type: 'MOBILE',
                value: mobileNumber,
                countryCode: `+${mobileCountryCode}`,
                action: 'UPDATE_MOBILE',
                otp: cleanedOtp
            }).unwrap()

            if (
                response.action?.toLowerCase() === 'signin' ||
                response.action?.toLowerCase() === 'signup' ||
                response.action?.toLowerCase() === 'update_mobile'
            ) {
                try {
                    // Fetch and map user details to ensure consistent auth state
                    const authState = await fetchAndMapUserDetails(() => fetchUserDetails().unwrap(), {
                        mobile: mobileNumber,
                        countryCode: mobileCountryCode,
                        email: googleUserData.email
                    })

                    // Add Google-specific fields to the auth state
                    const googleAuthState = {
                        ...authState,
                        picture: googleUserData.picture,
                        googleId: googleUserData.googleId,
                        userId: googleUserData.userId,
                        ctAuth: googleUserData.ctAuth
                    }

                    dispatch(setAuthUser(googleAuthState))
                    triggerCleverTapLogin()
                } catch (err: any) {
                    dispatch(
                        setAuthUser({
                            signedIn: true,
                            mobile: mobileNumber,
                            countryCode: mobileCountryCode,
                            email: googleUserData.email,
                            firstName: googleUserData.firstName,
                            lastName: googleUserData.lastName,
                            fullName: googleUserData.fullName,
                            picture: googleUserData.picture,
                            googleId: googleUserData.googleId,
                            userId: googleUserData.userId,
                            ctAuth: googleUserData.ctAuth,
                            displayName: googleUserData.fullName || googleUserData.email
                        })
                    )
                    triggerCleverTapLogin()
                }

                // Clear Google flow flag before closing to prevent cookie clearing
                setIsGoogleFlow(false)

                // Close menu first, then close modal to prevent flickering
                onMenuClose?.()
                handleClose()
                isVerifyingMobileRef.current = false
            } else if ((response as any).error) {
                const errorMapping = mapOtpError((response as any).error, t)
                setError(errorMapping.message)
                isVerifyingMobileRef.current = false
            }
        } catch (err: any) {
            handleOtpError(err)
            isVerifyingMobileRef.current = false
        }
    }, [
        mobileOtp,
        mobileNumber,
        mobileCountryCode,
        verifyOtp,
        dispatch,
        googleUserData,
        handleClose,
        onMenuClose,
        handleOtpError,
        t,
        clearError,
        setError,
        fetchUserDetails,
        fetchAndMapUserDetails,
        triggerCleverTapLogin
    ])

    // Handle resending mobile OTP in Google flow
    const handleResendGoogleMobileOtp = useCallback(async () => {
        if (mobileTimerRef.current.isRunning) return

        setMobileOtp('')
        clearError()

        try {
            const response = await sendOtp({
                type: 'MOBILE',
                value: mobileNumber,
                countryCode: `+${mobileCountryCode}`,
                action: 'UPDATE_MOBILE'
            }).unwrap()

            if (response.status?.toLowerCase() === 'success') {
                clearError()
                mobileTimerRef.current.start()
            } else if (response.error) {
                const errorMapping = mapOtpError(response.error, t)
                setError(errorMapping.message)
            }
        } catch (err: any) {
            handleApiError(err, t('failedToResendOtp'))
        }
    }, [mobileNumber, mobileCountryCode, sendOtp, handleApiError, t, clearError, setError])

    // Cleanup verification flags and timeouts on unmount
    useEffect(() => {
        return () => {
            isVerifyingMobileRef.current = false
            isVerifyingEmailRef.current = false

            // Clear any pending timeouts to prevent memory leaks
            if (mobileVerifyTimeoutRef.current) {
                clearTimeout(mobileVerifyTimeoutRef.current)
                mobileVerifyTimeoutRef.current = null
            }
            if (emailVerifyTimeoutRef.current) {
                clearTimeout(emailVerifyTimeoutRef.current)
                emailVerifyTimeoutRef.current = null
            }
        }
    }, [])

    // Auto-verify when OTP is complete
    useEffect(() => {
        if (mobileOtp.length === 4 && currentStep === OtpStep.MOBILE_VERIFICATION && !isVerifyingMobileRef.current) {
            if (isGoogleFlow) {
                handleVerifyGoogleMobileOtp()
            } else {
                handleVerifyMobileOtp()
            }
        }
    }, [mobileOtp, currentStep, isGoogleFlow])

    useEffect(() => {
        if (emailOtp.length === 4 && currentStep === OtpStep.EMAIL_VERIFICATION && !isVerifyingEmailRef.current) {
            handleVerifyEmailOtp()
        }
    }, [emailOtp, currentStep])

    return {
        // Flow state
        currentStep,
        error,
        errorState,
        isWelcomeBack,
        existingEmail,
        isGoogleFlow,
        googleUserData,

        // Mobile data
        mobileCountryCode,
        selectedCountryCode,
        countryData,
        isCountryDataLoading,
        mobileNumber,
        mobileOtp,

        // Email data
        email,
        emailOtp,

        // Loading states
        isSendingOtp,
        isVerifyingOtp,
        isSendingEmailOtp,
        isVerifyingEmailOtp,

        // Timer states
        mobileResendTimer: mobileTimer.timer,
        emailResendTimer: emailTimer.timer,

        // Handlers
        handleClose,
        handleOnCross,
        handleCountryCodeChange,
        handleMobileNumberChange,
        handleMobileOtpChange,
        handleSendMobileOtp,
        handleVerifyMobileOtp,
        handleResendMobileOtp,
        handleBackToMobileInput,
        handleEmailChange,
        handleEmailOtpChange,
        handleSendEmailOtp,
        handleVerifyEmailOtp,
        handleResendEmailOtp,
        handleBackToEmailInput,
        handleGoogleSignIn,
        handleGoogleSignInError,
        handleSendGoogleMobileOtp,
        handleVerifyGoogleMobileOtp,
        handleResendGoogleMobileOtp
    }
}
